/**
 * Call Sheet Editor — Task #429 two-tab record.
 *
 * Route: /call-sheet/:id
 *
 * Tab 1 "Callsheet" = roster (people + managed roles) + day schedule rendered
 *   as an Apple-Calendar-style overlapping timeline of time blocks, each with
 *   assigned roster people.
 * Tab 2 "Set List" = ordered performance segments with per-item notes + a
 *   total runtime (offering-derived default, manual override).
 *
 * Times (event time, load-in, block start/end) are stored as "HH:mm" clock
 * strings to avoid timezone drift; the calendar canvas works in
 * minutes-from-midnight.
 */
import { useState, useMemo } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  ArrowLeft,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  CloudSun,
  Clock,
  Users,
  ListMusic,
  Pencil,
  MessageSquare,
  CalendarClock,
  Share2,
  Copy,
  Lock,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import type {
  ShowCallSheet,
  CallSheetCollaborator,
  CallSheetRoster,
  CallSheetBlock,
  CallSheetSetItem,
  CallSheetSharedNote,
} from "@shared/schema";

type BlockWithAssignments = CallSheetBlock & { assignedRosterIds: string[] };

interface CallSheetPayload {
  callSheet: ShowCallSheet;
  canEdit: boolean;
  runtimeDefaultMinutes: number | null;
  collaborators: CallSheetCollaborator[];
  roster: CallSheetRoster[];
  blocks: BlockWithAssignments[];
  setItems: CallSheetSetItem[];
  sharedNotes: CallSheetSharedNote[];
}

interface EffectiveRole {
  id: string;
  source: "platform" | "workspace" | "talent";
  label: string;
}

interface RoleCatalog {
  platformRoles: { id: string; name: string }[];
  workspaceRoles: { id: string; name: string }[];
  hiddenPlatformRoleIds: string[];
  talentRoles: { id: string; name: string }[];
  talentHides: { roleId: string; roleSource: string }[];
}

// ── time helpers ──────────────────────────────────────────────────────
function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return format(dt, "EEE, MMM d, yyyy");
  } catch {
    return "—";
  }
}

function minutesFromClock(hhmm: string | null | undefined): number | null {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function fmtClock(hhmm: string | null | undefined): string {
  const mins = minutesFromClock(hhmm);
  if (mins == null) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

// Normalise a role token ("sound_tech", "Sound Tech") for fuzzy matching.
function normalizeRole(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

// Greedy lane assignment for an Apple-Calendar-style overlapping timeline.
// Returns each block annotated with {laneIndex, laneCount} for its cluster.
function layoutBlocks(blocks: BlockWithAssignments[]) {
  const items = blocks
    .map((b) => {
      const start = minutesFromClock(b.startTime);
      const end = minutesFromClock(b.endTime) ?? (start != null ? start + 30 : null);
      return { block: b, start: start ?? 0, end: end ?? (start ?? 0) + 30 };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const result: { block: BlockWithAssignments; start: number; end: number; lane: number; lanes: number }[] = [];
  let cluster: typeof result = [];
  let clusterEnd = -1;

  const flush = () => {
    const lanes = cluster.reduce((m, c) => Math.max(m, c.lane + 1), 0);
    for (const c of cluster) c.lanes = lanes;
    result.push(...cluster);
    cluster = [];
    clusterEnd = -1;
  };

  for (const it of items) {
    if (cluster.length && it.start >= clusterEnd) flush();
    // assign the lowest free lane within the cluster
    const used = new Set(cluster.filter((c) => c.end > it.start).map((c) => c.lane));
    let lane = 0;
    while (used.has(lane)) lane++;
    cluster.push({ ...it, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  if (cluster.length) flush();
  return result;
}

export default function CallSheetEditorPage() {
  const [, params] = useRoute<{ id: string }>("/call-sheet/:id");
  const callSheetId = params?.id;
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<CallSheetPayload>({
    queryKey: ["/api/call-sheets", callSheetId],
    queryFn: async () => {
      const r = await fetch(`/api/call-sheets/${callSheetId}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load call sheet");
      return r.json();
    },
    enabled: !!callSheetId,
    refetchInterval: 30_000,
  });

  const callSheet = data?.callSheet;
  const canEdit = data?.canEdit ?? false;
  const runtimeDefault = data?.runtimeDefaultMinutes ?? null;
  const talentId = (callSheet as any)?.talentId as string | null | undefined;

  // ── Effective role catalog (for roster role dropdown) ─────────────────
  const { data: catalog } = useQuery<RoleCatalog>({
    queryKey: ["/api/collaborator-roles", talentId ?? undefined],
    queryFn: async () => {
      const url = talentId
        ? `/api/collaborator-roles?talentId=${encodeURIComponent(talentId)}`
        : "/api/collaborator-roles";
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load roles");
      return r.json();
    },
    enabled: !!callSheetId,
  });

  // Talent address-book contacts (for "Add from contacts" with auto role).
  const { data: contacts } = useQuery<
    { id: string; name: string; role: string; company: string | null }[]
  >({
    queryKey: ["/api/talent-contacts", talentId ?? undefined],
    queryFn: async () => {
      const r = await fetch(
        `/api/talent-contacts?talentId=${encodeURIComponent(talentId!)}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("Failed to load contacts");
      return r.json();
    },
    enabled: !!callSheetId && !!talentId,
  });

  const effectiveRoles: EffectiveRole[] = useMemo(() => {
    if (!catalog) return [];
    const hidePlatform = new Set(catalog.hiddenPlatformRoleIds);
    const talentHides = new Set(catalog.talentHides.map((h) => `${h.roleSource}:${h.roleId}`));
    const out: EffectiveRole[] = [];
    for (const p of catalog.platformRoles) {
      if (hidePlatform.has(p.id)) continue;
      if (talentHides.has(`platform:${p.id}`)) continue;
      out.push({ id: p.id, source: "platform", label: p.name });
    }
    for (const w of catalog.workspaceRoles) {
      if (talentHides.has(`workspace:${w.id}`)) continue;
      out.push({ id: w.id, source: "workspace", label: w.name });
    }
    for (const t of catalog.talentRoles) {
      out.push({ id: t.id, source: "talent", label: t.name });
    }
    return out;
  }, [catalog]);

  const roster = useMemo(
    () => (data?.roster ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [data?.roster],
  );
  const blocks = data?.blocks ?? [];
  const setItems = useMemo(
    () => (data?.setItems ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [data?.setItems],
  );
  const sharedNotes = useMemo(
    () =>
      (data?.sharedNotes ?? [])
        .slice()
        .sort((a, b) => new Date(a.createdAt as any).getTime() - new Date(b.createdAt as any).getTime()),
    [data?.sharedNotes],
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/call-sheets", callSheetId] });

  // ── Header edits ──────────────────────────────────────────────────────
  const [headerEdits, setHeaderEdits] = useState<{
    title?: string;
    venueName?: string;
    venueAddress?: string;
    generalNotes?: string;
    eventTime?: string;
    loadInTime?: string;
  }>({});
  const headerDirty = Object.keys(headerEdits).length > 0;

  const saveHeader = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("PATCH", `/api/call-sheets/${callSheetId}`, headerEdits);
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      setHeaderEdits({});
      toast({ title: "Saved", description: "Header updated." });
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Save failed", variant: "destructive" }),
  });

  const togglePublish = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("PATCH", `/api/call-sheets/${callSheetId}`, {
        status: callSheet?.status === "published" ? "draft" : "published",
      });
      return r.json();
    },
    onSuccess: (row: any) => {
      invalidate();
      toast({
        title: row.status === "published" ? "Published" : "Unpublished",
        description:
          row.status === "published"
            ? "Collaborators can now view this call sheet."
            : "Collaborators can no longer view this call sheet.",
      });
    },
  });

  const deleteCallSheet = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/call-sheets/${callSheetId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/call-sheets"] });
      toast({ title: "Deleted", description: "Call sheet deleted." });
      navigate("/artist");
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Delete failed", variant: "destructive" }),
  });

  // ── Roster mutations ──────────────────────────────────────────────────
  const [rosterDialog, setRosterDialog] = useState<{ open: boolean; editing: CallSheetRoster | null }>({
    open: false,
    editing: null,
  });
  const [rosterForm, setRosterForm] = useState<{ name: string; roleKey: string; note: string }>({
    name: "",
    roleKey: "",
    note: "",
  });

  function openRosterDialog(row: CallSheetRoster | null) {
    if (row) {
      setRosterForm({
        name: row.name,
        roleKey: row.roleId && row.roleSource ? `${row.roleSource}:${row.roleId}` : "",
        note: row.note || "",
      });
    } else {
      setRosterForm({ name: "", roleKey: "", note: "" });
    }
    setRosterDialog({ open: true, editing: row });
  }

  const saveRoster = useMutation({
    mutationFn: async () => {
      const role = effectiveRoles.find((r) => `${r.source}:${r.id}` === rosterForm.roleKey);
      const body: any = {
        name: rosterForm.name,
        note: rosterForm.note || null,
        roleId: role?.id ?? null,
        roleSource: role?.source ?? null,
        roleLabel: role?.label ?? null,
      };
      if (rosterDialog.editing) {
        const r = await apiRequest(
          "PATCH",
          `/api/call-sheets/${callSheetId}/roster/${rosterDialog.editing.id}`,
          body,
        );
        return r.json();
      }
      body.personType = "manual";
      body.sortOrder = roster.length;
      const r = await apiRequest("POST", `/api/call-sheets/${callSheetId}/roster`, body);
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      setRosterDialog({ open: false, editing: null });
      toast({ title: "Saved", description: "Roster updated." });
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Save failed", variant: "destructive" }),
  });

  const deleteRoster = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/call-sheets/${callSheetId}/roster/${id}`);
    },
    onSuccess: () => invalidate(),
  });

  // Add a person straight from the talent address book, auto-resolving the
  // contact's free-form role against the effective catalog when it matches.
  const [contactPicker, setContactPicker] = useState(false);
  const addFromContact = useMutation({
    mutationFn: async (contact: { id: string; name: string; role: string }) => {
      const match = effectiveRoles.find(
        (r) => normalizeRole(r.label) === normalizeRole(contact.role),
      );
      const body: any = {
        personType: "contact",
        talentContactId: contact.id,
        name: contact.name,
        roleId: match?.id ?? null,
        roleSource: match?.source ?? null,
        roleLabel: match?.label ?? null,
        sortOrder: roster.length,
      };
      const r = await apiRequest("POST", `/api/call-sheets/${callSheetId}/roster`, body);
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Added", description: "Contact added to roster." });
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Add failed", variant: "destructive" }),
  });

  // ── Block mutations ───────────────────────────────────────────────────
  const [blockDialog, setBlockDialog] = useState<{ open: boolean; editing: CallSheetBlock | null }>({
    open: false,
    editing: null,
  });
  const [blockForm, setBlockForm] = useState<{
    label: string;
    startTime: string;
    endTime: string;
    location: string;
    note: string;
    assignedRosterIds: string[];
  }>({ label: "", startTime: "", endTime: "", location: "", note: "", assignedRosterIds: [] });

  function openBlockDialog(block: BlockWithAssignments | null) {
    if (block) {
      setBlockForm({
        label: block.label,
        startTime: block.startTime,
        endTime: block.endTime || "",
        location: block.location || "",
        note: block.note || "",
        assignedRosterIds: block.assignedRosterIds ?? [],
      });
    } else {
      setBlockForm({
        label: "",
        startTime: callSheet?.loadInTime || "",
        endTime: "",
        location: "",
        note: "",
        assignedRosterIds: [],
      });
    }
    setBlockDialog({ open: true, editing: block });
  }

  const saveBlock = useMutation({
    mutationFn: async () => {
      const body: any = {
        label: blockForm.label,
        startTime: blockForm.startTime,
        endTime: blockForm.endTime || null,
        location: blockForm.location || null,
        note: blockForm.note || null,
        assignedRosterIds: blockForm.assignedRosterIds,
      };
      if (blockDialog.editing) {
        const r = await apiRequest(
          "PATCH",
          `/api/call-sheets/${callSheetId}/blocks/${blockDialog.editing.id}`,
          body,
        );
        return r.json();
      }
      body.sortOrder = blocks.length;
      const r = await apiRequest("POST", `/api/call-sheets/${callSheetId}/blocks`, body);
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      setBlockDialog({ open: false, editing: null });
      toast({ title: "Saved", description: "Time block saved." });
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Save failed", variant: "destructive" }),
  });

  const deleteBlock = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/call-sheets/${callSheetId}/blocks/${id}`);
    },
    onSuccess: () => invalidate(),
  });

  // ── Set-item mutations ────────────────────────────────────────────────
  const [setItemDialog, setSetItemDialog] = useState<{ open: boolean; editing: CallSheetSetItem | null }>({
    open: false,
    editing: null,
  });
  const [setItemForm, setSetItemForm] = useState({ title: "", note: "" });

  function openSetItemDialog(item: CallSheetSetItem | null) {
    setSetItemForm(item ? { title: item.title, note: item.note || "" } : { title: "", note: "" });
    setSetItemDialog({ open: true, editing: item });
  }

  const saveSetItem = useMutation({
    mutationFn: async () => {
      const body: any = { title: setItemForm.title, note: setItemForm.note || null };
      if (setItemDialog.editing) {
        const r = await apiRequest(
          "PATCH",
          `/api/call-sheets/${callSheetId}/set-items/${setItemDialog.editing.id}`,
          body,
        );
        return r.json();
      }
      body.sortOrder = setItems.length;
      const r = await apiRequest("POST", `/api/call-sheets/${callSheetId}/set-items`, body);
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      setSetItemDialog({ open: false, editing: null });
      toast({ title: "Saved", description: "Set list updated." });
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Save failed", variant: "destructive" }),
  });

  const deleteSetItem = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/call-sheets/${callSheetId}/set-items/${id}`);
    },
    onSuccess: () => invalidate(),
  });

  const reorderSetItem = useMutation({
    mutationFn: async ({ id, sortOrder }: { id: string; sortOrder: number }) => {
      await apiRequest("PATCH", `/api/call-sheets/${callSheetId}/set-items/${id}`, { sortOrder });
    },
    onSuccess: () => invalidate(),
  });

  function moveSetItem(idx: number, dir: -1 | 1) {
    const a = setItems[idx];
    const b = setItems[idx + dir];
    if (!a || !b) return;
    reorderSetItem.mutate({ id: a.id, sortOrder: b.sortOrder });
    reorderSetItem.mutate({ id: b.id, sortOrder: a.sortOrder });
  }

  // ── Runtime override ──────────────────────────────────────────────────
  const [runtimeDraft, setRuntimeDraft] = useState<string | null>(null);
  const saveRuntime = useMutation({
    mutationFn: async (value: number | null) => {
      const r = await apiRequest("PATCH", `/api/call-sheets/${callSheetId}`, {
        runtimeOverrideMinutes: value,
      });
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      setRuntimeDraft(null);
      toast({ title: "Saved", description: "Runtime updated." });
    },
  });

  // ── Shared notes ──────────────────────────────────────────────────────
  const [noteDraft, setNoteDraft] = useState("");
  const postNote = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", `/api/call-sheets/${callSheetId}/shared-notes`, {
        body: noteDraft,
      });
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      setNoteDraft("");
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Post failed", variant: "destructive" }),
  });

  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/call-sheets/${callSheetId}/shared-notes/${id}`);
    },
    onSuccess: () => invalidate(),
  });

  // ── External collaborators (token-gated share links) ──────────────────
  const collaborators = data?.collaborators ?? [];
  const [collabDialog, setCollabDialog] = useState(false);
  const [collabForm, setCollabForm] = useState<{ name: string; email: string; roleKeys: string[] }>({
    name: "",
    email: "",
    roleKeys: [],
  });

  function shareUrl(token: string): string {
    return `${window.location.origin}/share/call-sheet/${token}`;
  }

  const copyShareLink = (token: string) => {
    const url = shareUrl(token);
    navigator.clipboard
      ?.writeText(url)
      .then(() => toast({ title: "Copied", description: "Share link copied to clipboard." }))
      .catch(() =>
        toast({ title: "Share link", description: url }),
      );
  };

  const inviteCollab = useMutation({
    mutationFn: async () => {
      const roleLabels = collabForm.roleKeys
        .map((k) => effectiveRoles.find((r) => `${r.source}:${r.id}` === k)?.label)
        .filter((l): l is string => !!l);
      const r = await apiRequest("POST", `/api/call-sheets/${callSheetId}/collaborators`, {
        name: collabForm.name,
        email: collabForm.email || undefined,
        roles: roleLabels,
      });
      return r.json();
    },
    onSuccess: (row: any) => {
      invalidate();
      setCollabDialog(false);
      setCollabForm({ name: "", email: "", roleKeys: [] });
      if (row?.accessToken) copyShareLink(row.accessToken);
      toast({ title: "Invited", description: "Share link created and copied." });
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Invite failed", variant: "destructive" }),
  });

  const revokeCollab = useMutation({
    mutationFn: async (collabId: string) => {
      await apiRequest("DELETE", `/api/call-sheets/${callSheetId}/collaborators/${collabId}`);
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Revoked", description: "Share link disabled." });
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message || "Revoke failed", variant: "destructive" }),
  });

  if (isLoading || !callSheet) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const weather: any = (callSheet as any).weatherCache;
  const rosterById = new Map(roster.map((r) => [r.id, r]));
  const laidOut = layoutBlocks(blocks);
  // Default visible window = the event-day buffer envelope: load-in start →
  // event time + the runtime/post-event buffer. Blocks scheduled outside that
  // envelope still render — we expand the window to include them.
  const runtimeMinutes = callSheet.runtimeOverrideMinutes ?? runtimeDefault;
  const loadInMin = minutesFromClock(callSheet.loadInTime);
  const eventMin = minutesFromClock(callSheet.eventTime);
  const bufferStart = loadInMin ?? eventMin;
  const bufferEnd =
    eventMin != null ? eventMin + (runtimeMinutes != null ? runtimeMinutes : 120) : null;
  const candidateStarts = [
    ...(bufferStart != null ? [bufferStart] : []),
    ...laidOut.map((b) => b.start),
  ];
  const candidateEnds = [
    ...(bufferEnd != null ? [bufferEnd] : []),
    ...laidOut.map((b) => b.end),
  ];
  const windowStart =
    candidateStarts.length > 0 ? Math.max(0, Math.min(...candidateStarts) - 30) : 0;
  const windowEnd =
    candidateEnds.length > 0
      ? Math.min(24 * 60, Math.max(...candidateEnds, windowStart + 60) + 30)
      : 0;
  const PX_PER_MIN = 1.1;
  const canvasHeight = Math.max(0, (windowEnd - windowStart) * PX_PER_MIN);
  // Hour gridlines within the window.
  const hourLines: number[] = [];
  for (let m = Math.ceil(windowStart / 60) * 60; m <= windowEnd; m += 60) hourLines.push(m);

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-4 max-w-4xl">
      <div className="flex items-center justify-between gap-2">
        <Link href="/artist">
          <Button variant="ghost" size="sm" data-testid="link-back-portal">
            <ArrowLeft className="h-4 w-4 mr-1" /> Portal
          </Button>
        </Link>
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setDeleteConfirm(true)}
            data-testid="button-delete-callsheet"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        ) : (
          <Badge variant="outline" className="gap-1" data-testid="badge-view-only">
            <Lock className="h-3 w-3" /> View only
          </Badge>
        )}
      </div>

      {/* ── Header card ───────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-xl sm:text-2xl break-words" data-testid="text-callsheet-title">
                {callSheet.title}
              </CardTitle>
              <CardDescription className="mt-1">
                {fmtDate(callSheet.eventDate as any)}
                {callSheet.eventTime ? ` • Event ${fmtClock(callSheet.eventTime)}` : ""}
                {callSheet.venueName ? ` • ${callSheet.venueName}` : ""}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant={callSheet.status === "published" ? "default" : "outline"}
                data-testid="badge-callsheet-status"
              >
                {callSheet.status}
              </Badge>
              {canEdit && (
                <Button
                  size="sm"
                  variant={callSheet.status === "published" ? "outline" : "default"}
                  onClick={() => togglePublish.mutate()}
                  disabled={togglePublish.isPending}
                  data-testid="button-toggle-publish"
                >
                  {callSheet.status === "published" ? "Unpublish" : "Publish"}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cs-title">Title</Label>
              <Input
                id="cs-title"
                value={headerEdits.title ?? callSheet.title}
                onChange={(e) => setHeaderEdits({ ...headerEdits, title: e.target.value })}
                disabled={!canEdit}
                data-testid="input-callsheet-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cs-event-time">Event Time</Label>
                <Input
                  id="cs-event-time"
                  type="time"
                  value={headerEdits.eventTime ?? (callSheet.eventTime || "")}
                  onChange={(e) => setHeaderEdits({ ...headerEdits, eventTime: e.target.value })}
                  disabled={!canEdit}
                  data-testid="input-callsheet-eventtime"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cs-loadin">Load-in</Label>
                <Input
                  id="cs-loadin"
                  type="time"
                  value={headerEdits.loadInTime ?? (callSheet.loadInTime || "")}
                  onChange={(e) => setHeaderEdits({ ...headerEdits, loadInTime: e.target.value })}
                  disabled={!canEdit}
                  data-testid="input-callsheet-loadin"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cs-venue">Venue Name</Label>
              <Input
                id="cs-venue"
                value={headerEdits.venueName ?? (callSheet.venueName || "")}
                onChange={(e) => setHeaderEdits({ ...headerEdits, venueName: e.target.value })}
                disabled={!canEdit}
                data-testid="input-callsheet-venue"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cs-addr">Venue Address</Label>
              <Input
                id="cs-addr"
                value={headerEdits.venueAddress ?? (callSheet.venueAddress || "")}
                onChange={(e) => setHeaderEdits({ ...headerEdits, venueAddress: e.target.value })}
                disabled={!canEdit}
                data-testid="input-callsheet-address"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cs-notes">General Notes (visible to all)</Label>
            <Textarea
              id="cs-notes"
              rows={2}
              value={headerEdits.generalNotes ?? (callSheet.generalNotes || "")}
              onChange={(e) => setHeaderEdits({ ...headerEdits, generalNotes: e.target.value })}
              disabled={!canEdit}
              data-testid="input-callsheet-notes"
            />
          </div>
          {headerDirty && (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setHeaderEdits({})}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => saveHeader.mutate()}
                disabled={saveHeader.isPending}
                data-testid="button-save-header"
              >
                {saveHeader.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                Save Header
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Weather widget ────────────────────────────────────────────── */}
      {weather && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CloudSun className="h-4 w-4" />
              Weather — {weather?.location?.name || "Event day forecast"}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {weather?.current && (
              <p>
                <span className="font-medium">{Math.round(weather.current.temperature_2m)}°F</span>
                {" • "}wind {Math.round(weather.current.wind_speed_10m)} mph
                {" • "}humidity {weather.current.relative_humidity_2m}%
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Two-tab record ────────────────────────────────────────────── */}
      <Tabs defaultValue="callsheet" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="callsheet" data-testid="tab-callsheet">
            <Users className="h-4 w-4 mr-2" /> Callsheet
          </TabsTrigger>
          <TabsTrigger value="setlist" data-testid="tab-setlist">
            <ListMusic className="h-4 w-4 mr-2" /> Set List
          </TabsTrigger>
        </TabsList>

        {/* ── TAB 1: CALLSHEET ──────────────────────────────────────── */}
        <TabsContent value="callsheet" className="space-y-4 mt-4">
          {/* Roster */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Roster</CardTitle>
                <CardDescription>People on this call sheet and their roles.</CardDescription>
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  {talentId && (contacts?.length ?? 0) > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setContactPicker(true)}
                      data-testid="button-add-from-contacts"
                    >
                      <Users className="h-4 w-4 mr-1" /> From Contacts
                    </Button>
                  )}
                  <Button size="sm" onClick={() => openRosterDialog(null)} data-testid="button-add-roster">
                    <Plus className="h-4 w-4 mr-1" /> Person
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {roster.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="text-no-roster">
                  No one on the roster yet.
                </p>
              )}
              {roster.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-2 border rounded-lg p-3 group"
                  data-testid={`row-roster-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{p.name}</span>
                      {p.roleLabel && (
                        <Badge variant="secondary" className="text-xs">
                          {p.roleLabel}
                        </Badge>
                      )}
                    </div>
                    {p.note && <p className="text-xs text-muted-foreground mt-1">{p.note}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => openRosterDialog(p)}
                        data-testid={`button-edit-roster-${p.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => deleteRoster.mutate(p.id)}
                        data-testid={`button-delete-roster-${p.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Day schedule — overlapping calendar */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarClock className="h-5 w-5" /> Day Schedule
                </CardTitle>
                <CardDescription>Time blocks with assigned people.</CardDescription>
              </div>
              {canEdit && (
                <Button size="sm" onClick={() => openBlockDialog(null)} data-testid="button-add-block">
                  <Plus className="h-4 w-4 mr-1" /> Block
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {blocks.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="text-no-blocks">
                  No time blocks yet. Add load-in, sound check, doors, set times, etc.
                </p>
              ) : (
                <div className="flex gap-2">
                  {/* hour gutter */}
                  <div className="relative w-12 shrink-0" style={{ height: canvasHeight }}>
                    {hourLines.map((m) => (
                      <div
                        key={m}
                        className="absolute right-1 -translate-y-1/2 text-[10px] text-muted-foreground"
                        style={{ top: (m - windowStart) * PX_PER_MIN }}
                      >
                        {fmtClock(`${String(Math.floor(m / 60)).padStart(2, "0")}:00`)}
                      </div>
                    ))}
                  </div>
                  {/* canvas */}
                  <div
                    className="relative flex-1 border-l border-border"
                    style={{ height: canvasHeight }}
                    data-testid="calendar-canvas"
                  >
                    {hourLines.map((m) => (
                      <div
                        key={m}
                        className="absolute left-0 right-0 border-t border-border/50"
                        style={{ top: (m - windowStart) * PX_PER_MIN }}
                      />
                    ))}
                    {laidOut.map(({ block, start, end, lane, lanes }) => {
                      const top = (start - windowStart) * PX_PER_MIN;
                      const height = Math.max(22, (end - start) * PX_PER_MIN - 2);
                      const widthPct = 100 / lanes;
                      const names = block.assignedRosterIds
                        .map((id) => rosterById.get(id)?.name)
                        .filter(Boolean);
                      return (
                        <button
                          key={block.id}
                          onClick={() => openBlockDialog(block)}
                          className="absolute rounded-md border border-primary/40 bg-primary/10 hover:bg-primary/20 text-left px-2 py-1 overflow-hidden transition-colors"
                          style={{
                            top,
                            height,
                            left: `calc(${lane * widthPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                          }}
                          data-testid={`block-${block.id}`}
                        >
                          <p className="text-xs font-medium truncate">{block.label}</p>
                          <p className="text-[10px] text-muted-foreground truncate">
                            {fmtClock(block.startTime)}
                            {block.endTime ? `–${fmtClock(block.endTime)}` : ""}
                          </p>
                          {names.length > 0 && (
                            <p className="text-[10px] truncate">{names.join(", ")}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shared notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" /> Shared Notes
              </CardTitle>
              <CardDescription>Notes posted by the team. Everyone with access can post.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {sharedNotes.map((n) => (
                <div key={n.id} className="border rounded-lg p-3 group" data-testid={`row-note-${n.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium">{n.authorName}</p>
                      <p className="text-sm whitespace-pre-wrap mt-0.5">{n.body}</p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => deleteNote.mutate(n.id)}
                      data-testid={`button-delete-note-${n.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex gap-2">
                <Textarea
                  rows={2}
                  placeholder="Add a note for the team…"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  data-testid="input-new-note"
                />
                <Button
                  onClick={() => noteDraft.trim() && postNote.mutate()}
                  disabled={!noteDraft.trim() || postNote.isPending}
                  data-testid="button-post-note"
                >
                  {postNote.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB 2: SET LIST ───────────────────────────────────────── */}
        <TabsContent value="setlist" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Set List</CardTitle>
                <CardDescription>Ordered performance segments.</CardDescription>
              </div>
              {canEdit && (
                <Button size="sm" onClick={() => openSetItemDialog(null)} data-testid="button-add-setitem">
                  <Plus className="h-4 w-4 mr-1" /> Segment
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex flex-wrap items-center gap-2 pb-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="cs-runtime" className="text-sm">
                  Total runtime (minutes)
                </Label>
                <Input
                  id="cs-runtime"
                  type="number"
                  min={0}
                  className="h-8 w-24"
                  placeholder={runtimeDefault != null ? String(runtimeDefault) : "—"}
                  value={runtimeDraft ?? (callSheet.runtimeOverrideMinutes ?? "").toString()}
                  onChange={(e) => setRuntimeDraft(e.target.value)}
                  disabled={!canEdit}
                  data-testid="input-runtime"
                />
                {canEdit && runtimeDraft !== null && (
                  <Button
                    size="sm"
                    onClick={() =>
                      saveRuntime.mutate(runtimeDraft.trim() === "" ? null : Number(runtimeDraft))
                    }
                    disabled={saveRuntime.isPending}
                    data-testid="button-save-runtime"
                  >
                    {saveRuntime.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                  </Button>
                )}
                <span className="text-xs text-muted-foreground" data-testid="text-runtime-effective">
                  {callSheet.runtimeOverrideMinutes != null
                    ? `${callSheet.runtimeOverrideMinutes} min`
                    : runtimeDefault != null
                      ? `${runtimeDefault} min (offering default)`
                      : "not set"}
                </span>
              </div>

              {setItems.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="text-no-setitems">
                  No segments yet.
                </p>
              )}
              {setItems.map((item, idx) => (
                <div
                  key={item.id}
                  className="flex items-start gap-2 border rounded-lg p-3 group"
                  data-testid={`row-setitem-${item.id}`}
                >
                  {canEdit && (
                    <div className="flex flex-col">
                      <button
                        onClick={() => moveSetItem(idx, -1)}
                        disabled={idx === 0}
                        className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                        data-testid={`button-move-up-setitem-${item.id}`}
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => moveSetItem(idx, 1)}
                        disabled={idx === setItems.length - 1}
                        className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                        data-testid={`button-move-down-setitem-${item.id}`}
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  <span className="text-sm text-muted-foreground w-5 pt-0.5">{idx + 1}.</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{item.title}</p>
                    {item.note && <p className="text-xs text-muted-foreground mt-0.5">{item.note}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => openSetItemDialog(item)}
                        data-testid={`button-edit-setitem-${item.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => deleteSetItem.mutate(item.id)}
                        data-testid={`button-delete-setitem-${item.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── External collaborators (share links) ──────────────────────── */}
      {canEdit && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="h-5 w-5" /> External Collaborators
              </CardTitle>
              <CardDescription>
                Share a read-only link with people outside your team. Links only resolve while the
                call sheet is published.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setCollabDialog(true)} data-testid="button-invite-collab">
              <Plus className="h-4 w-4 mr-1" /> Invite
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {collaborators.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="text-no-collabs">
                No collaborators yet.
              </p>
            )}
            {collaborators.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-2 border rounded-lg p-3"
                data-testid={`row-collab-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{c.name}</span>
                    {(c.roles ?? []).map((r) => (
                      <Badge key={r} variant="secondary" className="text-xs">
                        {r}
                      </Badge>
                    ))}
                  </div>
                  {c.email && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{c.email}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {c.viewCount > 0
                      ? `${c.viewCount} view${c.viewCount === 1 ? "" : "s"}`
                      : "Not viewed yet"}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyShareLink(c.accessToken)}
                    data-testid={`button-copy-collab-${c.id}`}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" /> Link
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => revokeCollab.mutate(c.id)}
                    data-testid={`button-revoke-collab-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── Collaborator invite dialog ────────────────────────────────── */}
      <Dialog open={collabDialog} onOpenChange={setCollabDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Collaborator</DialogTitle>
            <DialogDescription>Creates a revocable read-only share link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="collab-name">Name</Label>
              <Input
                id="collab-name"
                value={collabForm.name}
                onChange={(e) => setCollabForm({ ...collabForm, name: e.target.value })}
                data-testid="input-collab-name"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="collab-email">Email (optional)</Label>
              <Input
                id="collab-email"
                type="email"
                value={collabForm.email}
                onChange={(e) => setCollabForm({ ...collabForm, email: e.target.value })}
                data-testid="input-collab-email"
              />
            </div>
            {effectiveRoles.length > 0 && (
              <div className="space-y-1.5">
                <Label>Roles (optional — controls section visibility)</Label>
                <div className="border rounded-lg p-2 space-y-1.5 max-h-40 overflow-auto">
                  {effectiveRoles.map((r) => {
                    const key = `${r.source}:${r.id}`;
                    const checked = collabForm.roleKeys.includes(key);
                    return (
                      <label
                        key={key}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                        data-testid={`check-collab-role-${key}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(c) =>
                            setCollabForm({
                              ...collabForm,
                              roleKeys: c
                                ? [...collabForm.roleKeys, key]
                                : collabForm.roleKeys.filter((k) => k !== key),
                            })
                          }
                        />
                        <span>{r.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCollabDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => inviteCollab.mutate()}
              disabled={!collabForm.name.trim() || inviteCollab.isPending}
              data-testid="button-save-collab"
            >
              {inviteCollab.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Create Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Roster dialog ─────────────────────────────────────────────── */}
      <Dialog open={rosterDialog.open} onOpenChange={(o) => setRosterDialog({ open: o, editing: o ? rosterDialog.editing : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{rosterDialog.editing ? "Edit Person" : "Add Person"}</DialogTitle>
            <DialogDescription>Add someone to the roster and assign a role.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="roster-name">Name</Label>
              <Input
                id="roster-name"
                value={rosterForm.name}
                onChange={(e) => setRosterForm({ ...rosterForm, name: e.target.value })}
                data-testid="input-roster-name"
              />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select
                value={rosterForm.roleKey || "none"}
                onValueChange={(v) => setRosterForm({ ...rosterForm, roleKey: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="select-roster-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No role</SelectItem>
                  {effectiveRoles.map((r) => (
                    <SelectItem key={`${r.source}:${r.id}`} value={`${r.source}:${r.id}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="roster-note">Note</Label>
              <Textarea
                id="roster-note"
                rows={2}
                value={rosterForm.note}
                onChange={(e) => setRosterForm({ ...rosterForm, note: e.target.value })}
                data-testid="input-roster-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRosterDialog({ open: false, editing: null })}>
              Cancel
            </Button>
            <Button
              onClick={() => saveRoster.mutate()}
              disabled={!rosterForm.name.trim() || saveRoster.isPending}
              data-testid="button-save-roster"
            >
              {saveRoster.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Contact picker ────────────────────────────────────────────── */}
      <Dialog open={contactPicker} onOpenChange={setContactPicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add from Contacts</DialogTitle>
            <DialogDescription>
              Pick a contact from the address book. Their role is matched to your catalog
              automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-80 overflow-auto">
            {(contacts ?? []).map((c) => {
              const already = roster.some((r) => r.talentContactId === c.id);
              return (
                <button
                  key={c.id}
                  disabled={already || addFromContact.isPending}
                  onClick={() => addFromContact.mutate(c)}
                  className="w-full flex items-center justify-between gap-2 border rounded-lg p-2.5 text-left hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`pick-contact-${c.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    {c.company && <p className="text-xs text-muted-foreground truncate">{c.company}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary" className="text-xs">
                      {c.role}
                    </Badge>
                    {already && <span className="text-xs text-muted-foreground">added</span>}
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContactPicker(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Block dialog ──────────────────────────────────────────────── */}
      <Dialog open={blockDialog.open} onOpenChange={(o) => setBlockDialog({ open: o, editing: o ? blockDialog.editing : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{blockDialog.editing ? "Edit Time Block" : "Add Time Block"}</DialogTitle>
            <DialogDescription>Set a call time or time range and assign people.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="block-label">Label</Label>
              <Input
                id="block-label"
                placeholder="e.g. Sound Check"
                value={blockForm.label}
                onChange={(e) => setBlockForm({ ...blockForm, label: e.target.value })}
                disabled={!canEdit}
                data-testid="input-block-label"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="block-start">Start</Label>
                <Input
                  id="block-start"
                  type="time"
                  value={blockForm.startTime}
                  onChange={(e) => setBlockForm({ ...blockForm, startTime: e.target.value })}
                  disabled={!canEdit}
                  data-testid="input-block-start"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="block-end">End (optional)</Label>
                <Input
                  id="block-end"
                  type="time"
                  value={blockForm.endTime}
                  onChange={(e) => setBlockForm({ ...blockForm, endTime: e.target.value })}
                  disabled={!canEdit}
                  data-testid="input-block-end"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="block-location">Location</Label>
              <Input
                id="block-location"
                value={blockForm.location}
                onChange={(e) => setBlockForm({ ...blockForm, location: e.target.value })}
                disabled={!canEdit}
                data-testid="input-block-location"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="block-note">Note</Label>
              <Textarea
                id="block-note"
                rows={2}
                value={blockForm.note}
                onChange={(e) => setBlockForm({ ...blockForm, note: e.target.value })}
                disabled={!canEdit}
                data-testid="input-block-note"
              />
            </div>
            {roster.length > 0 && (
              <div className="space-y-1.5">
                <Label>Assigned people</Label>
                <div className="border rounded-lg p-2 space-y-1.5 max-h-40 overflow-auto">
                  {roster.map((p) => {
                    const checked = blockForm.assignedRosterIds.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                        data-testid={`check-assign-${p.id}`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={!canEdit}
                          onCheckedChange={(c) =>
                            setBlockForm({
                              ...blockForm,
                              assignedRosterIds: c
                                ? [...blockForm.assignedRosterIds, p.id]
                                : blockForm.assignedRosterIds.filter((id) => id !== p.id),
                            })
                          }
                        />
                        <span>{p.name}</span>
                        {p.roleLabel && (
                          <Badge variant="outline" className="text-[10px]">
                            {p.roleLabel}
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between">
            {canEdit && blockDialog.editing ? (
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => {
                  deleteBlock.mutate(blockDialog.editing!.id);
                  setBlockDialog({ open: false, editing: null });
                }}
                data-testid="button-delete-block"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setBlockDialog({ open: false, editing: null })}>
                {canEdit ? "Cancel" : "Close"}
              </Button>
              {canEdit && (
                <Button
                  onClick={() => saveBlock.mutate()}
                  disabled={!blockForm.label.trim() || !blockForm.startTime || saveBlock.isPending}
                  data-testid="button-save-block"
                >
                  {saveBlock.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  Save
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Set-item dialog ───────────────────────────────────────────── */}
      <Dialog open={setItemDialog.open} onOpenChange={(o) => setSetItemDialog({ open: o, editing: o ? setItemDialog.editing : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{setItemDialog.editing ? "Edit Segment" : "Add Segment"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="setitem-title">Title</Label>
              <Input
                id="setitem-title"
                value={setItemForm.title}
                onChange={(e) => setSetItemForm({ ...setItemForm, title: e.target.value })}
                data-testid="input-setitem-title"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="setitem-note">Note</Label>
              <Textarea
                id="setitem-note"
                rows={2}
                value={setItemForm.note}
                onChange={(e) => setSetItemForm({ ...setItemForm, note: e.target.value })}
                data-testid="input-setitem-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSetItemDialog({ open: false, editing: null })}>
              Cancel
            </Button>
            <Button
              onClick={() => saveSetItem.mutate()}
              disabled={!setItemForm.title.trim() || saveSetItem.isPending}
              data-testid="button-save-setitem"
            >
              {saveSetItem.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirm ────────────────────────────────────────────── */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this call sheet?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the call sheet, its roster, schedule, set list, and shared
              notes. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-callsheet">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteCallSheet.mutate()}
              data-testid="button-confirm-delete-callsheet"
            >
              {deleteCallSheet.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
